/**
 * Один диспетчер гарячих клавіш (UX-11; карта — docs/ux/design-system/keyboard.md). Екрани й
 * компоненти реєструють клавіші через `useHotkeys`, а цей файл не правлять: один обробник
 * `keydown` на документі, реєстр з областями, з якого ж будуються шпаргалка `?` і команди палітри
 * `Ctrl+K` (тож вони не розходяться з реальними клавішами).
 *
 * Правила (keyboard.md, розд. 5):
 * - фізична клавіша (`event.code`), тож `C` працює й у кириличній розкладці;
 * - однолітерна — будь-яка символьна клавіша без `Ctrl`/`⌘` (літера, цифра, `[`, `/`, `?`…): не діє
 *   в полях введення й у відкритому меню і вимикається перемикачем профілю (WCAG 2.1.4, PRF-08);
 * - IME (`isComposing`, `keyCode` 229) і автоповтор (`repeat`) ігноруються;
 * - області за пріоритетом: модальний шар (палітра, діалог) → компонент у фокусі (`within`) →
 *   екран (`screen`) → глобальні (`global`). Поки відкритий модальний шар, клавіші під ним мовчать.
 *   У межах області перемагає пізніше зареєстрована. Конфлікти прив'язок ловить keymap.test.ts.
 * - послідовність `G` → літера: вікно 1.5 с, поки воно відкрите, послідовність має перевагу.
 *
 * Компоненти зі своєю навігацією (таблиця, «Сьогодні») обробляють клавіші самі в `onKeyDown` за
 * правилом `focusKeyAllowed` і реєструють їх тут лише підписами для шпаргалки (без `handler`).
 */
import { type RefObject, useEffect, useLayoutEffect, useRef, useSyncExternalStore } from 'react';
import { getPrefs } from '../prefs/prefs.ts';

/** Група команди в палітрі: контекст запису чи екрана, переходи, решта команд. */
export type CommandGroup = 'record' | 'screen' | 'nav' | 'command';

export interface Hotkey {
  /** `KeyC`, `Enter`, `Escape`, `BracketRight`…; без неї — лише команда палітри. */
  code?: string;
  /** Ctrl на Windows / ⌘ на macOS. */
  mod?: boolean;
  /** `Shift` (`?` — це `Shift` + `/`). */
  shift?: boolean;
  /** `Alt` (`Option` на macOS): `Alt+1`…`9`, `Alt+↓`. */
  alt?: boolean;
  /** Той самий символ з інших розкладок (`event.key`): `?` в українській — `Shift` + `7`. */
  char?: string;
  /** Послідовність: клавіша діє лише одразу після `prefix` (`KeyG` → `KeyH`). */
  prefix?: string;
  /** Дозволити, коли фокус у полі (лише `Esc`, `Ctrl+Enter`, `Ctrl+K`, `F8`). */
  inInput?: boolean;
  /** Без обробника — лише підпис для шпаргалки (клавішу обробляє компонент сам). */
  handler?: (e?: KeyboardEvent) => void;
  /** Підпис дії для шпаргалки й палітри; без нього клавіша в них не показується. */
  label?: string;
  /** Показати в палітрі як команду. `id` — для «Нещодавніх» (типово — підпис). */
  command?: { id?: string; group?: CommandGroup; keywords?: readonly string[] };
}

export type HotkeyScope = 'global' | 'screen';

export interface HotkeyOptions {
  /** `screen` (типово) — клавіші екрана; `global` — лише оболонки платформи. */
  scope?: HotkeyScope;
  enabled?: boolean;
  /** Компонент у фокусі: клавіші діють, лише коли фокус усередині елемента. */
  within?: RefObject<HTMLElement | null>;
  /** Модальний шар: клавіші діють, лише коли фокус у ньому; клавіші під шаром мовчать. */
  layer?: RefObject<HTMLElement | null>;
}

const TEXT_INPUTS = new Set([
  'text',
  'search',
  'email',
  'tel',
  'url',
  'number',
  'password',
  'date',
  'time',
  'datetime-local',
  'month',
  'week',
]);

/** Поле, яке саме обробляє символьні клавіші: тут однолітерні мовчать. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) return true;
  if (target instanceof HTMLInputElement) return TEXT_INPUTS.has(target.type);
  return target.matches('[role="combobox"], [role="textbox"], [role="searchbox"]');
}

/**
 * Не символьні клавіші: WCAG 2.1.4 на них не поширюється, перемикач профілю їх не вимикає.
 * `Space` — теж: у списку це стандартний вибір рядка (сітка ARIA).
 */
const NON_CHARACTER = new Set([
  'Escape',
  'Enter',
  'NumpadEnter',
  'Tab',
  'Space',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
  'PageUp',
  'PageDown',
  'Backspace',
  'Delete',
  'Insert',
]);

/** Однолітерна клавіша: символьна (не з NON_CHARACTER і не F1…F24) і без `Ctrl`/`⌘`/`Alt`. */
export function isSingleKey(key: Pick<Hotkey, 'code' | 'mod' | 'alt'>): boolean {
  return (
    Boolean(key.code) &&
    !key.mod &&
    !key.alt &&
    !NON_CHARACTER.has(key.code ?? '') &&
    !/^F\d{1,2}$/.test(key.code ?? '')
  );
}

/** Чи відкрите меню з власними клавішами: тоді однолітерні глобальні мовчать. */
function insideMenu(target: EventTarget | null) {
  return target instanceof HTMLElement && target.closest('[role="menu"]') !== null;
}

/** Введення через IME: перше й останнє натискання Chrome дає з `keyCode` 229 (MDN). */
const composing = (e: KeyboardEvent) => e.isComposing || e.keyCode === 229;

/** Однолітерна зараз дозволена: без IME й автоповтору, перемикач профілю увімкнено. */
function singleAllowed(e: KeyboardEvent) {
  return !composing(e) && !e.repeat && getPrefs().singleKeys;
}

/** Клавіша й модифікатори збігаються (без правил контексту). */
function chordMatches(e: KeyboardEvent, key: Hotkey): boolean {
  if (!key.code) return false;
  const byCode = e.code === key.code;
  const byChar = key.char !== undefined && e.key === key.char;
  if (!byCode && !byChar) return false;
  if (Boolean(key.mod) !== (e.ctrlKey || e.metaKey) || Boolean(key.alt) !== e.altKey) return false;
  // Символ уже містить Shift своєї розкладки; за фізичною клавішею Shift має збігтися.
  return byChar || Boolean(key.shift) === e.shiftKey;
}

/** Глобальна клавіша — це дія: автоповтор затиснутої клавіші її не повторює. */
export function matches(e: KeyboardEvent, key: Hotkey): boolean {
  if (e.repeat || !chordMatches(e, key)) return false;
  if (isSingleKey(key) && (!singleAllowed(e) || insideMenu(e.target))) return false;
  if (isTypingTarget(e.target) && !key.inInput) return false;
  return true;
}

/** Навігація, яку затиснута клавіша повторює: крок за кроком по списку. */
const REPEATABLE = new Set([
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'PageUp',
  'PageDown',
]);

/**
 * Клавіша області у фокусі (список, «Сьогодні»): символьні — за правилами глобальних
 * однолітерних, у тому числі перемикач профілю; навігаційні повторюються, поки затиснуті;
 * решта (Enter, Space, Esc) — один раз на натискання.
 */
export function focusKeyAllowed(e: KeyboardEvent): boolean {
  if (e.ctrlKey || e.metaKey || e.altKey || composing(e)) return false;
  if (isSingleKey({ code: e.code })) return !e.shiftKey && singleAllowed(e);
  return !e.repeat || REPEATABLE.has(e.code);
}

/**
 * Shift + клавіша області (розширити вибір у списку, LST-36): символьні — за правилами однолітерних
 * (перемикач профілю, без автоповтору), стрілки повторюються, поки затиснуті.
 */
export function focusShiftKeyAllowed(e: KeyboardEvent): boolean {
  if (!e.shiftKey || e.ctrlKey || e.metaKey || e.altKey || composing(e)) return false;
  if (isSingleKey({ code: e.code })) return singleAllowed(e);
  return !e.repeat || REPEATABLE.has(e.code);
}

// ————————————————————————————— Реєстр —————————————————————————————

const TIER = { layer: 0, within: 1, screen: 2, global: 3 } as const;
export type HotkeyTier = keyof typeof TIER;

interface Entry {
  keys: readonly Hotkey[];
  tier: HotkeyTier;
  within: RefObject<HTMLElement | null> | undefined;
  layer: RefObject<HTMLElement | null> | undefined;
  seq: number;
}

const entries = new Set<Entry>();
let seq = 0;

/** Відкритий модальний шар (палітра, шпаргалка, діалог незбережених змін). */
const MODAL = '[aria-modal="true"]';

/** Відкритий модальний шар: `aria-modal` або `<dialog>`, відкритий `showModal()` без атрибута. */
function modalOpen(): boolean {
  if (document.querySelector(MODAL)) return true;
  try {
    return document.querySelector('dialog:modal') !== null;
  } catch {
    // Середовище без псевдокласу `:modal` (jsdom): лишається перевірка за `aria-modal`.
    return false;
  }
}

const contains = (ref: RefObject<HTMLElement | null> | undefined, target: EventTarget | null) =>
  target instanceof Node && Boolean(ref?.current?.contains(target));

/** Реєстрації, які діють для цілі події, — за пріоритетом області, новіші першими. */
function activeEntries(target: EventTarget | null): Entry[] {
  const modal = modalOpen();
  return [...entries]
    .filter((entry) => {
      if (entry.tier === 'layer') return contains(entry.layer, target);
      if (modal) return false;
      if (entry.tier === 'within') return contains(entry.within, target);
      return true;
    })
    .sort((a, b) => TIER[a.tier] - TIER[b.tier] || b.seq - a.seq);
}

// —————————————————————— Послідовності (G → H) ——————————————————————

export const SEQUENCE_MS = 1500;
let sequenceMs = SEQUENCE_MS;

/** Для тестів: коротше вікно послідовності, щоб не чекати 1,5 с справжнього часу. */
export function setSequenceWindow(ms = SEQUENCE_MS) {
  sequenceMs = ms;
}

let pending: string | null = null;
let pendingTimer: ReturnType<typeof setTimeout> | undefined;
const sequenceListeners = new Set<() => void>();

function setPending(code: string | null) {
  clearTimeout(pendingTimer);
  pending = code;
  if (code) pendingTimer = setTimeout(() => setPending(null), sequenceMs);
  for (const l of sequenceListeners) l();
}

/** Префікс відкритої послідовності (`KeyG`) — для підказки внизу ліворуч; `null` — немає. */
function subscribeSequence(l: () => void) {
  sequenceListeners.add(l);
  return () => {
    sequenceListeners.delete(l);
  };
}
const getPending = () => pending;
const noPending = () => null;

export function useHotkeySequence(): string | null {
  return useSyncExternalStore(subscribeSequence, getPending, noPending);
}

/** Для тестів: закрити вікно послідовності. */
export const cancelHotkeySequence = () => setPending(null);

const MODIFIER = /^(Shift|Control|Alt|Meta|OS|CapsLock|Fn)/;

function run(e: KeyboardEvent, key: Hotkey) {
  e.preventDefault();
  key.handler?.(e);
}

function onKeyDown(e: KeyboardEvent) {
  if (e.defaultPrevented || composing(e) || MODIFIER.test(e.code)) return;
  const active = activeEntries(e.target);
  const keys = active.flatMap((entry) => entry.keys);

  if (pending) {
    const prefix = pending;
    setPending(null);
    // Друга клавіша послідовності — за правилами однолітерних (поле, меню, перемикач профілю).
    const next = keys.find((k) => k.prefix === prefix && k.handler && matches(e, k));
    if (next) return run(e, next);
    // `Esc` лише закриває підказку послідовності, а не діє далі (наприклад, «назад до списку»).
    if (e.code === 'Escape') return e.preventDefault();
  }

  const key = keys.find((k) => !k.prefix && k.handler && matches(e, k));
  if (key) return run(e, key);

  if (keys.some((k) => k.prefix === e.code) && matches(e, { code: e.code })) {
    e.preventDefault();
    setPending(e.code);
  }
}

let listening = false;

/**
 * Реєструє клавіші області на час життя компонента. Масив можна будувати на кожному рендері:
 * реєстрація одна, обробники читаються з останнього рендеру.
 */
export function useHotkeys(keys: readonly Hotkey[], options: HotkeyOptions = {}) {
  const { scope = 'screen', enabled = true, within, layer } = options;
  const tier: HotkeyTier = layer ? 'layer' : within ? 'within' : scope;
  const entry = useRef<Entry | null>(null);
  entry.current ??= { keys, tier, within, layer, seq: 0 };
  // Обробники з останнього рендеру — після коміту, а не під час рендеру (той може не закінчитись).
  useLayoutEffect(() => {
    (entry.current as Entry).keys = keys;
  });
  useEffect(() => {
    if (!enabled) return;
    if (!listening && typeof document !== 'undefined') {
      document.addEventListener('keydown', onKeyDown);
      listening = true;
    }
    const current = entry.current as Entry;
    current.tier = tier;
    current.within = within;
    current.layer = layer;
    current.seq = ++seq;
    entries.add(current);
    return () => {
      entries.delete(current);
    };
  }, [enabled, tier, within, layer]);
}

export interface RegisteredHotkey {
  key: Hotkey;
  tier: HotkeyTier;
}

/**
 * Зареєстровані клавіші поза модальними шарами — для шпаргалки й палітри, за пріоритетом області.
 * Клавіші компонента у фокусі (`within`) входять завжди: шпаргалка показує клавіші всього екрана.
 */
export function registeredHotkeys(): RegisteredHotkey[] {
  return [...entries]
    .filter((entry) => entry.tier !== 'layer')
    .sort((a, b) => TIER[a.tier] - TIER[b.tier] || b.seq - a.seq)
    .flatMap((entry) => entry.keys.map((key) => ({ key, tier: entry.tier })));
}

// ————————————————————————————— Підписи —————————————————————————————

export const isMac = () =>
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

/**
 * Підказка комбінації з `Ctrl` / `⌘` у нотації ОС користувача (keyboard.md, п. 5): на macOS —
 * «⌘K», «⌘↩» (без «+»), деінде — «Ctrl+K», «Ctrl+Enter».
 */
export const kbdLabel = (key: string) =>
  isMac() ? `⌘${key === 'Enter' ? '↩' : key}` : `Ctrl+${key}`;

/**
 * Те саме словами для програми читання: «⌘↩» вона читає незрозуміло (рев'ю S4), тож на macOS —
 * «Command Return», деінде — як `kbdLabel` («Ctrl+Enter» читається й так). Показ — `ModKbd`.
 */
export const kbdSpoken = (key: string) =>
  isMac() ? `Command ${key === 'Enter' ? 'Return' : key}` : kbdLabel(key);

const CODE_LABEL: Record<string, string> = {
  BracketLeft: '[',
  BracketRight: ']',
  Slash: '/',
  Backslash: '\\',
  Period: '.',
  Comma: ',',
  Semicolon: ';',
  Quote: "'",
  Backquote: '`',
  Minus: '-',
  Equal: '=',
  Escape: 'Esc',
  Space: 'Space',
  ArrowUp: '↑',
  ArrowDown: '↓',
  ArrowLeft: '←',
  ArrowRight: '→',
  PageUp: 'PageUp',
  PageDown: 'PageDown',
  Backspace: 'Backspace',
  Delete: 'Delete',
};

/** Підпис фізичної клавіші латиницею: `KeyC` → «C», `Digit1` → «1», `BracketRight` → «]». */
export function codeLabel(code: string): string {
  if (/^Key[A-Z]$/.test(code)) return code.slice(3);
  if (/^(Digit|Numpad)\d$/.test(code)) return code.slice(-1);
  if (code === 'Enter' || code === 'NumpadEnter') return isMac() ? '↩' : 'Enter';
  return CODE_LABEL[code] ?? code;
}

/** Як назвати клавішу словами для програми читання: «⌘» вона читає незрозуміло. */
export const SPOKEN: Record<string, string> = {
  '⌘': 'Command',
  '⌥': 'Option',
  '↩': 'Return',
  '↑': 'Стрілка вгору',
  '↓': 'Стрілка вниз',
  '←': 'Стрілка ліворуч',
  '→': 'Стрілка праворуч',
};

/**
 * Клавіші прив'язки для показу — кожна окремою `<kbd>`: `['Ctrl', 'K']`, `['⌘', 'K']`,
 * `['G', 'H']` для послідовності, `['?']` для `Shift` + `/`.
 */
export function chordKeys(key: Hotkey): string[] {
  if (!key.code) return [];
  const parts: string[] = [];
  if (key.prefix) parts.push(codeLabel(key.prefix));
  if (key.mod) parts.push(isMac() ? '⌘' : 'Ctrl');
  if (key.alt) parts.push(isMac() ? '⌥' : 'Alt');
  if (key.code === 'Slash' && key.shift && !key.mod) return [...parts, '?'];
  if (key.shift) parts.push('Shift');
  parts.push(codeLabel(key.code));
  return parts;
}
